
import { delay, concat, mergeMap, switchMap, mapTo, first, filter } from 'rxjs/operators'
import { Epic } from 'interfaces/middleware'
import { SET_MEMBER_INFO } from 'actions/memberActions'
import {
  FLUSH_LIVE_UPDATE,
  flushLiveUpdateActionCreator,
  POLL_LIVE_UPDATE,
  pollLiveUpdateActionCreator,
  pollProviderAccountActionCreator,
  GET_MIXED_ACCOUNTS,
} from 'actions/linkageActions'
import { combineEpics } from 'redux-observable'
import { FLUSH_LIVE_UPDATE_URL, GET_LIVE_UPDATE_INFO_URL } from 'interfaces/ajax'
import { get, post } from 'helpers/observableAjaxHelpers'
import { Observable, of, empty, from } from 'rxjs'
import { Action } from 'actions'
import { liveUpdateSelector, providerAccountStatesSelector, providerAccountSelector } from 'selectors/linkageSelectors'
import { addAccomplishmentActionCreator } from 'actions/bountyActions'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { catchAjaxSafe } from 'rxOperators/catchSafe'
import { ProviderAccountState } from 'interfaces/linkage'

const LIVE_UPDATE_POLL_DELAY = 5000

const startLiveUpdateEpic: Epic = (action$, store) =>
  action$.pipe(
    filter((a) =>
      a.type === SET_MEMBER_INFO && a.payload.onboarding_step === 'finished'
    ),
    first(),
    mapTo(pollLiveUpdateActionCreator.request({
      isRepeatedRequest: false,
      info: liveUpdateSelector(store.getState()),
    })))

const pollLiveUpdateEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(POLL_LIVE_UPDATE, 'request')).pipe(
      switchMap((action) => {
        const { params } = action.payload
        const { request, success, fail } = pollLiveUpdateActionCreator

        return get(GET_LIVE_UPDATE_INFO_URL).pipe(
          mergeMap(({ response }) => {
            const { total_count, status } = response
            let result$: Observable<Action> = of(success(params, response))
            if (total_count > 0 && status === 'started') {
              result$ = result$.pipe(concat(
                of(
                  request({
                    isRepeatedRequest: true,
                    info: liveUpdateSelector(store.getState()),
                  })
                ).pipe(delay(LIVE_UPDATE_POLL_DELAY))
              ))
            }

            return result$
          }))
          .pipe(catchAjaxSafe(() => of(fail(params))))
      }))

const liveUpdateSuccessEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(POLL_LIVE_UPDATE, 'success')).pipe(
      filter((action) => action.payload.response.status === 'started'),
      switchMap(() => action$
        .pipe(filterRequestOfType(POLL_LIVE_UPDATE, 'success')).pipe(
          filter((action) => action.payload.response.status !== 'started'),
          switchMap(() => action$
            .pipe(filterRequestOfType(GET_MIXED_ACCOUNTS, 'success')).pipe(
              mergeMap(() => {
                const state = store.getState()
                const providerAccountStatesCollection = providerAccountStatesSelector(state)
                if (!providerAccountStatesCollection) {
                  return empty()
                }

                const actions = getItemsFromCollection<ProviderAccountState, 'cid'>(providerAccountStatesCollection)
                  .filter((providerAccountState) => providerAccountState.status === 'updating')
                  .map((providerAccountState) => providerAccountSelector(state, providerAccountState))
                  .filter((providerAccount): providerAccount is NonNullable<typeof providerAccount> => !!providerAccount)
                  .map((providerAccount) => pollProviderAccountActionCreator.request(providerAccount))

                if (actions.length === 0) {
                  return empty()
                }

                return of(...actions)
              }))
          ))
      ))

const flushLiveUpdateEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(FLUSH_LIVE_UPDATE, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { success, fail } = flushLiveUpdateActionCreator

        return post(FLUSH_LIVE_UPDATE_URL).pipe(
          mergeMap(({ response }) => {
            const actions: Action[] = []

            if (response.accomplishment) {
              actions.push(addAccomplishmentActionCreator(response.accomplishment))
            }

            actions.push(success(params, response))

            return from(actions)
          }))
          .pipe(catchAjaxSafe(() => of(fail(params))))
      }))

export default combineEpics(
  startLiveUpdateEpic,
  pollLiveUpdateEpic,
  flushLiveUpdateEpic,
  liveUpdateSuccessEpic,
)
